import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock, Upload } from "lucide-react";

const DocRow = ({ d, token, onDone }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_key", d.key);
    [...files].forEach((f) => fd.append("files", f));
    try { onDone((await api.post(`/docs-upload/${token}`, fd)).data); toast.success("Uploaded - thank you!"); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-4" data-testid={`upload-row-${d.key}`}>
      <div className="min-w-[200px] flex-1 text-sm text-slate-800">
        {d.received && <CheckCircle2 className="mr-1 inline h-4 w-4 text-emerald-600" />}{d.label}
        {d.uploaded > 0 && <div className="text-xs text-slate-500">{d.uploaded} file{d.uploaded > 1 ? "s" : ""} uploaded</div>}
      </div>
      <label className="cursor-pointer">
        <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`upload-input-${d.key}`} />
        <span className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-semibold ${d.received ? "border border-slate-300 text-slate-700" : "gold-cta"}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" />{d.received ? "Add more" : "Upload"}</>}</span>
      </label>
    </div>
  );
};

export default function UploadDocs() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/docs-upload/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiError(e))); }, [token]);
  const done = info?.docs.every((d) => d.received);
  return (
    <div className="min-h-screen bg-[#37494d] px-4 py-10">
      <div className="mx-auto max-w-2xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8" data-testid="upload-page">
        <div className="mb-6"><Brand /></div>
        {err ? <p className="text-sm text-rose-600" data-testid="upload-error">{err}</p> : !info ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
          <>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Congrats, {info.first_name}!</h1>
            <p className="mb-1 text-sm text-slate-600">Upload the documents {info.mlo_name} needs to finish your approval. PDF, JPG or PNG, up to 10 MB each.</p>
            <p className="mb-5 flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" />Your files go privately to your loan officer only.</p>
            {done && <div className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800" data-testid="upload-all-done">Every document is in. Thank you!</div>}
            <div className="space-y-3">{info.docs.map((d) => <DocRow key={d.key} d={d} token={token} onDone={setInfo} />)}</div>
          </>
        )}
      </div>
    </div>
  );
}
